import { useState } from 'react';
import { Copy, Check, Server, Info } from 'lucide-react';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuthStore } from '@/stores/auth';
import { toast } from 'sonner';

export function MyMCPConfigPage() {
  const { user } = useAuthStore();
  const [copied, setCopied] = useState(false);

  if (!user) {
    return (
      <LayoutWithSidebar title="我的 MCP 配置">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>请先登录查看 MCP 配置</AlertDescription>
        </Alert>
      </LayoutWithSidebar>
    );
  }

  // 获取当前域名和端口
  const currentHost = window.location.hostname;
  const currentPort = window.location.port || (window.location.protocol === 'https:' ? '443' : '80');
  const baseUrl = currentPort === '80' || currentPort === '443' 
    ? `${window.location.protocol}//${currentHost}`
    : `${window.location.protocol}//${currentHost}:${currentPort}`;

  // 构建 MCP 配置
  const mcpConfig = {
    mcpServers: {
      'mcp-hub': {
        url: `${baseUrl}/mcp?userId=${user.id}`,
      },
    },
  };

  // Claude Desktop 配置（使用 npx 方式）
  const claudeDesktopConfig = {
    mcpServers: {
      'mcp-hub': {
        command: 'npx',
        args: [
          '-y',
          'node-mcp-client',
          `${baseUrl}/mcp?userId=${user.id}`,
        ],
      },
    },
  };

  const configJson = JSON.stringify(mcpConfig, null, 2);
  const claudeConfigJson = JSON.stringify(claudeDesktopConfig, null, 2);

  const handleCopy = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${type}已复制到剪贴板`);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('复制失败，请手动复制');
    }
  };

  return (
    <LayoutWithSidebar title="我的 MCP 配置">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">我的 MCP 配置</h1>
        <p className="text-gray-600 dark:text-gray-400">
          配置您的 MCP 客户端以访问订阅的工具
        </p>
      </div>

      {/* 用户信息 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            连接信息
          </CardTitle>
          <CardDescription>您的个人 MCP 服务器连接信息</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">用户名</label>
            <p className="mt-1 text-base font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded">
              {user.username}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">用户 ID</label>
            <p className="mt-1 text-base font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded break-all">
              {user.id}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">MCP 端点</label>
            <p className="mt-1 text-base font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded break-all">
              {baseUrl}/mcp?userId={user.id}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* 标准 MCP 配置 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>标准 MCP 客户端配置</CardTitle>
          <CardDescription>
            适用于支持 HTTP 传输的 MCP 客户端（如Alita Planet 客户端）
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
              <code>{configJson}</code>
            </pre>
            <Button
              size="sm"
              variant="secondary"
              className="absolute top-2 right-2"
              onClick={() => handleCopy(configJson, '配置')}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  已复制
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-1" />
                  复制
                </>
              )}
            </Button>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              将此配置添加到您的 MCP 客户端配置文件中（通常是 <code>mcp-config.json</code> 或 <code>claude_desktop_config.json</code>）
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Claude Desktop 配置 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Claude Desktop 配置</CardTitle>
          <CardDescription>
            使用 node-mcp-client 代理访问 MCP Hub（推荐用于 Claude Desktop）
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
              <code>{claudeConfigJson}</code>
            </pre>
            <Button
              size="sm"
              variant="secondary"
              className="absolute top-2 right-2"
              onClick={() => handleCopy(claudeConfigJson, 'Claude Desktop 配置')}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  已复制
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-1" />
                  复制
                </>
              )}
            </Button>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <p className="mb-2">将此配置添加到 Claude Desktop 的配置文件：</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>macOS: <code>~/Library/Application Support/Claude/claude_desktop_config.json</code></li>
                <li>Windows: <code>%APPDATA%\Claude\claude_desktop_config.json</code></li>
              </ul>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* 使用说明 */}
      <Card>
        <CardHeader>
          <CardTitle>使用说明</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h3 className="font-semibold">1. 订阅工具</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              在"工具市场"页面订阅您需要的工具
            </p>
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold">2. 配置客户端</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              复制上方配置并添加到您的 MCP 客户端配置文件中
            </p>
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold">3. 重启客户端</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              重启您的 MCP 客户端以加载新配置
            </p>
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold">4. 开始使用</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              现在您可以在客户端中使用订阅的工具了！
            </p>
          </div>

          <Alert className="bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800">
            <Info className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
            <AlertDescription className="text-yellow-800 dark:text-yellow-200">
              <strong>安全提示：</strong>您的 userId 是唯一的，请不要分享给他人。只有持有您的 userId 的客户端才能访问您订阅的工具。
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </LayoutWithSidebar>
  );
}

